// FAQSection.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'Can you guarantee that I will be admitted?',
      answer: 'No, and nobody honestly can. Every admission decision belongs to the institution and is taken against standards it sets for itself. What we control is the application: we build one that competes properly and steer you through the stages that suit your profile. Where we think your chances are thin, we will tell you at the time rather than let you discover it late. Presenting you at your strongest is our job; the verdict is not ours, and the process is sounder for that.'
    },
    {
      question: 'Does the first consultation cost anything?',
      answer: 'It is free, and nothing follows from it unless you want it to. We use the time to look over your academic record, hear which countries and courses interest you, and work out which programmes you are genuinely eligible for. By the end you should understand how the process runs, roughly how long it takes, and which options are realistic for someone with your background. Bring questions, because it works better as a two way conversation, and it also tells us whether we are the right people to help you.'
    },
    {
      question: 'Do you work directly with universities?',
      answer: 'Yes. We hold working relationships with academic institutions in the United States, Canada, the United Kingdom, Australia and Europe, built up through repeated collaboration and placements that went well. Those links mean we can put an application forward with better context, read what a particular institution is looking for, and at times flag a scholarship or a fee reduction. What they cannot do is bend an admission standard or secure you a place. Whatever the relationship, you still have to meet the academic and eligibility requirements that each institution sets.'
    },
    {
      question: 'Is there any help available with scholarships?',
      answer: 'We look for what is there. Partner institutions run scholarships and discounted tuition arrangements that eligible candidates can be put forward for, and how far a fee is reduced depends on your academic record, the programme you pick, and what the institution still has open when you apply. None of it is promised and there is real competition for it. We check where you stand during the consultation and pursue the funding routes that suit your circumstances. Any recommendation we make is explained to you and follows the criteria the institution has published.'
    },
    {
      question: 'Who actually writes my Statement of Purpose?',
      answer: 'It is a joint effort, drafted and tightened by our team with you closely involved throughout. The substance has to come from you: your reasons for applying, what you have done so far, and where you expect the qualification to lead. Our part is shaping that material into something structured and readable that matches what your target institutions look for. The finished statement should still sound like you, because the account is yours; what we add is drafting experience and a sense of what admissions teams respond to.'
    },
    {
      question: 'Where does my tuition payment go?',
      answer: 'Straight to the institution. Tuition and any other school charges are paid to the university itself and never to Ivipass. We do not take in, hold, or pass on that money for anyone, which keeps your payments both traceable and safe. Once an offer arrives and you accept it, you deal with the institution over how much is due and when, using whatever methods it accepts. We are glad to explain a fee structure or a payment schedule if it is unclear, but the transaction stays between you and the university.'
    },
    {
      question: 'Do I deal with the university myself?',
      answer: 'You do. As soon as contact is opened and the application is in, the formal exchanges are yours: decisions on admission, any scholarship award, enrolment instructions and every official letter, all issued in your name. That does not leave you on your own. If a message is unclear or you are unsure what it asks of you, we will read it with you and set out the next move, and we stay involved through enrolment and the preparation that follows. Think of us as an advisor beside you rather than a middleman sitting between you and the institution.'
    },
    {
      question: 'Am I limited to a single institution?',
      answer: 'Not at all, and spreading your applications is usually the smarter move. Several submissions running at once improves the odds of an offer and leaves you comparing choices instead of waiting on one answer. Depending on your record, the countries that appeal to you and the subject you want, we may suggest a set of institutions and run those applications in parallel. The point is to have a fallback and a genuine decision to make at the end. We help you pick a sensible spread against your goals and your eligibility, then keep each one moving.'
    },
    {
      question: 'What if I have no idea whether I qualify?',
      answer: 'Then the free consultation is doing precisely the job it exists for. We go through your qualifications, your academic record, any work experience that counts toward it, your English language level and anything else that bears on the decision. What comes back is a realistic read on which programmes and which countries are open to you. Where something is missing or too weak, we name it and tell you what would fix it. We would rather hand you an uncomfortable answer at the start than let a gap surface halfway through an application.'
    },
    {
      question: 'Which levels of study do you cover?',
      answer: 'Undergraduate degrees, postgraduate study taking in Masters and PhD routes, and a selection of professional programmes across a range of fields. We adjust what we do to the level, because entry criteria, the documents required and the shape of the application itself differ a good deal between them. In the consultation we work out which level matches your qualifications and your aims, then make sure you understand its particular requirements and dates. As with everything else, support depends on your eligibility and on what the institution asks for.'
    },
    {
      question: 'What happens if I walk away after the consultation?',
      answer: 'Nothing at all. You are under no obligation once the session ends. Its whole purpose is to let you gather information, see the options laid out, and judge whether what we offer matches what you need. Deciding against it is a perfectly ordinary outcome and we will not lean on you over it, beyond a single courtesy message to see where you landed. Whatever guidance you picked up in the session is yours to use however you like. We would rather you leave with a clear head than sign up for something that was never right for you.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600 text-sm font-light max-w-2xl mx-auto">
            Straight answers to what candidates ask us most often about studying abroad
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="border border-gray-200 rounded-lg overflow-hidden"
            >
              {/* Question Button */}
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-surface transition-colors"
              >
                <span className="text-base font-semibold text-primary pr-4">
                  {faq.question}
                </span>
                <div className="flex-shrink-0">
                  {openIndex === index ? (
                    <Minus size={20} className="text-primary" />
                  ) : (
                    <Plus size={20} className="text-primary" />
                  )}
                </div>
              </button>

              {/* Answer */}
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="p-6 pt-0 bg-white">
                      <p className="text-gray-600 text-sm font-light leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
